import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';

import apiClient from '@src/api/clients';

const KIND_LABELS = {
  contract_sent: 'Contract sent',
  contract_reminder: 'Contract reminder',
  contract_signed: 'Contract signed',
  welcome: 'Welcome + portal login',
  digest: 'Follow-up: missing items',
  assets_nudge: 'Assets reminder',
  card_request: 'Ad account card request',
  creative_ready: 'Creative ready for review',
  ready_to_launch: 'Ready to launch',
  campaigns_live: 'Campaigns live',
  first_leads: 'First leads',
  escalation: 'Escalated to the team',
  launch_escalation: 'Launch stuck — told the team',
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Every onboarding email we sent this client, newest first. All of them sit in
// one Gmail thread from clients@leadgurus.com, so replies reach Jack and Will.
export default function OnboardingEmailHistoryCard({ clientSlug }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!clientSlug) return;
    apiClient
      .request({ url: 'tasks/onboarding-emails/', method: 'GET', payload: { client: clientSlug } })
      .then((res) => setRows(res?.data || []))
      .catch(() => setRows([]));
  }, [clientSlug]);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700}>Emails sent</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          One thread from clients@leadgurus.com. Client replies go to Jack and Will.
        </Typography>
        {rows === null && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
        {rows?.length === 0 && (
          <Typography variant="body2" color="text.secondary">No onboarding emails logged yet.</Typography>
        )}
        <Stack spacing={1}>
          {(rows || []).map((r) => (
            <Box key={r.id} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, fontVariantNumeric: 'tabular-nums' }}>
                {fmtDate(r.sent_at)}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                  {KIND_LABELS[r.kind] || r.kind}
                  {r.progress != null && (
                    <Typography component="span" variant="caption" color="text.secondary">{` · ${r.progress}% done`}</Typography>
                  )}
                </Typography>
                {!r.is_internal && r.to?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {r.to.join(', ')}
                  </Typography>
                )}
              </Box>
              {r.error && <Chip size="small" color="error" label="Failed" />}
              {r.is_internal && <Chip size="small" label="Team" />}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

OnboardingEmailHistoryCard.propTypes = {
  clientSlug: PropTypes.string,
};
